<template>
  <span>
    <!-- ▼メッセージ作成-->
    <applicant-message-register-form
      v-show="!isSendable"
      ref="applicantMessageRegisterForm"
      v-model="message"
      :entry-ids="entryIds"
      @filledOut="onFilledOut"
    />
    <!-- ▲メッセージ作成-->
    <!-- ▼メッセージ確認-->
    <applicant-message-register-confirm
      v-show="isSendable"
      :message="confirmMessage"
      @back="isSendable = false"
      @submitted="onRegistered"
      @reserved="onReserved"
    />
    <!-- ▲メッセージ確認-->
  </span>
</template>

<script>
import ApplicantMessageRegisterForm from '@/components/molecules/ApplicantMessageRegisterForm'
import ApplicantMessageRegisterConfirm from '@/components/molecules/ApplicantMessageRegisterConfirm'
import _ from 'lodash'
import Indicator from '@/lib/indicator'

function createMessage (entryIds, messageToApplicant) {
  return {
    entryIds: entryIds,
    entryMessageId: messageToApplicant?.entryMessageId,
    subject: messageToApplicant?.message.subject ?? '',
    body: messageToApplicant?.message.body ?? '',
    files: messageToApplicant?.files ?? [],
    schedule: {
      candidateDates: messageToApplicant?.interviewAdjustment?.candidateDates ?? [],
      isUseCompanyAddress: messageToApplicant?.interviewAdjustment?.isUseCompanyAddress ?? true,
      place: messageToApplicant?.interviewAdjustment?.isUseCompanyAddress
        ? null
        : messageToApplicant?.interviewAdjustment?.place,
      duration: messageToApplicant?.interviewAdjustment?.duration,
      contentIds: messageToApplicant?.interviewAdjustment?.contentList.map(content => content.id) ?? [],
      otherContents: messageToApplicant?.interviewAdjustment?.contentOther ?? '',
      remarks: messageToApplicant?.interviewAdjustment?.remarks ?? ''
    },
    isPhaseChange: messageToApplicant?.selection?.isPhaseChange ?? null,
    phaseId: messageToApplicant?.selection?.phase?.id,
    isStatusChange: messageToApplicant?.selection?.isStatusChange ?? null,
    statusId: messageToApplicant?.selection?.status?.id
  }
}

export default {
  components: {
    ApplicantMessageRegisterForm,
    ApplicantMessageRegisterConfirm
  },
  props: {
    entryIds: {
      type: Array,
      required: true
    },
    messageToApplicant: {
      type: Object,
      default: () => { return null }
    }
  },
  data () {
    return {
      message: createMessage(this.entryIds, this.messageToApplicant),
      isCandidateDate: false,
      isSendable: false,
      totalEntryCountSum: 0,
      executeEntryCountSum: 0
    }
  },
  computed: {
    confirmMessage () {
      return this.isCandidateDate ? this.message : _.set(_.cloneDeep(this.message), 'schedule', null)
    },
    fileIds () {
      return this.message.files.map(file => file.id)
    },
    hasSameFileName () {
      return this.message.files.length !== _.uniqBy(this.message.files, 'name').length
    },
    isNoExecute () {
      return this.executeEntryCountSum === 0
    },
    isAllExecute () {
      return this.executeEntryCountSum === this.totalEntryCountSum
    },
    executeResultLabel () {
      return `${this.totalEntryCountSum}名中${this.executeEntryCountSum}名`
    },
    sendExecuteTargetLabel () {
      if (this.message.isPhaseChange && this.message.isStatusChange) return '送信・選考フェーズ、ステータス更新'
      if (this.message.isPhaseChange) return '送信・選考フェーズ更新'
      if (this.message.isStatusChange) return '送信・選考ステータス更新'
      return '送信'
    }
  },
  watch: {
    entryIds: {
      handler () {
        this.fetchCurrentSelection()
      }
    },
    async messageToApplicant (val) {
      this.$set(this, 'message', createMessage(this.entryIds, val))
      await this.fetchCurrentSelection()

      // フェーズ・ステータスに指定がある場合は上書き
      if (val?.selection?.isPhaseChange) this.$set(this.message, 'phaseId', val?.selection?.phase?.id)
      if (val?.selection?.isStatusChange) this.$set(this.message, 'statusId', val?.selection?.status?.id)
    }
  },
  created () {
    this.entryIds.length && this.fetchCurrentSelection()
  },
  methods: {
    onFilledOut (isCandidateDate) {
      this.isCandidateDate = isCandidateDate
      this.isSendable = true
    },
    showSameFileNameConfirmDialog (actionName, callback) {
      this.$dialog.confirm(`同じファイル名のものが複数添付されています。このまま${actionName}してよいですか？`)
        // はいを押下した時
        .then(() => {
          callback()
        })
        // いいえを押下した時（catch句を消すとコンソールに警告が出るから消さないでね！！）
        .catch(() => {})
    },
    executeChunkedOperation (operationCallback) {
      const chunkEntryIds = _.chunk(this.confirmMessage.entryIds, 50)
      operationCallback(chunkEntryIds)
    },
    executeApiOperation (chunkEntryIds, config, recursiveCallback) {
      const entryIds = chunkEntryIds.shift()
      // リクエストパラメータを整形
      const requestParam = this.requestMessageParam(entryIds)
      Indicator.emitShow()

      // メッセージIDの有無によってAPIパスを切り替える
      // - メッセージIDを持つ（=予約中編集）場合は応募IDは必ず1件
      const entryMessageId = this.confirmMessage.entryMessageId
      const apiPath = entryMessageId ? config.reservedApiPath : config.normalApiPath

      this.$http.secured.post(apiPath, requestParam).then((response) => {
        this.$set(this, 'totalEntryCountSum', this.totalEntryCountSum + response.data.totalEntryCount)
        this.$set(this, 'executeEntryCountSum', this.executeEntryCountSum + response.data[config.responseCountField])

        if (_.isEmpty(chunkEntryIds)) {
          if (this.isNoExecute) {
            this.$toasted.global.error({ message: `退会・予約中の応募者のため、${config.operationName}できません。` })
            this.resetCountSum()
          } else if (this.isAllExecute) {
            this.$toasted.global.success({ message: `${this.executeResultLabel}の${config.actionName}が完了しました。` })
            this.successTransition()
            this.reset()
          } else {
            this.$toasted.global.success({ message: `${this.executeResultLabel}の${config.actionName}が完了しました。<br>※退会・予約中の応募者には${config.operationName}できません。` })
            this.successTransition()
            this.reset()
          }
          Indicator.emitHide()
        } else {
          recursiveCallback(chunkEntryIds)
        }
      }).catch(() => {
        Indicator.emitHide()
      })
    },
    onRegistered () {
      this.hasSameFileName
        ? this.showSameFileNameConfirmDialog('送信', this.chunkSend)
        : this.chunkSend()
    },
    chunkSend () {
      this.executeChunkedOperation(this.send)
    },
    send (chunkEntryIds) {
      this.executeApiOperation(chunkEntryIds, {
        reservedApiPath: `/entry/message/send/entries/${this.entryIds[0]}/message/send`,
        normalApiPath: '/entry/message/send',
        responseCountField: 'sendEntryCount',
        operationName: '送信',
        actionName: this.sendExecuteTargetLabel
      }, this.send)
    },
    onReserved () {
      this.hasSameFileName
        ? this.showSameFileNameConfirmDialog('送信予約', this.chunkReserve)
        : this.chunkReserve()
    },
    chunkReserve () {
      this.executeChunkedOperation(this.reserve)
    },
    reserve (chunkEntryIds) {
      this.executeApiOperation(chunkEntryIds, {
        reservedApiPath: `/entry/message/send/entries/${this.entryIds[0]}/message/reserved_reservation`,
        normalApiPath: '/entry/message/send/entries/message/reserved_reservation/bulk_register',
        responseCountField: 'reserveEntryCount',
        operationName: '設定',
        actionName: '予約'
      }, this.reserve)
    },
    async fetchCurrentSelection () {
      const entryId = this.entryIds[0]
      await this.$http.secured.get(`/entry/view/entries/${entryId}/current_selection`).then(response => {
        this.$set(this.message, 'phaseId', response.data.phaseId)
        this.$set(this.message, 'statusId', response.data.statusId)
      })
    },
    reset () {
      this.$set(this, 'message', createMessage(this.entryIds, null))
      this.resetCountSum()
      this.fetchCurrentSelection()
      this.$refs.applicantMessageRegisterForm.reset()
      this.$scrollTo('#message-register', 0, { offset: -35 })
    },
    resetCountSum () {
      this.$set(this, 'totalEntryCountSum', 0)
      this.$set(this, 'executeEntryCountSum', 0)
    },
    requestMessageParam (entryIds) {
      // 添付ファイル情報を除き、添付ファイルIDの配列を付与
      const requestParam = _.omit(_.cloneDeep(this.confirmMessage), ['files'])
      _.set(requestParam, 'fileIds', this.fileIds)

      // 応募ID一覧を追加
      requestParam.entryIds = entryIds || []

      // 選考フェーズ・ステータスを変更しない場合はパラメータを削除
      if (!requestParam.isPhaseChange) delete requestParam.phaseId
      if (!requestParam.isStatusChange) delete requestParam.statusId

      return requestParam
    },
    successTransition () {
      if (this.$route.params.entryId) {
        this.isSendable = false
        this.$emit('registered')
      } else {
        // 一括の場合
        this.$router.push('/applicants/')
      }
    }
  }
}
</script>
